/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Dashboard shell — Lit light-DOM component that owns the post-login page
 * chrome (header, navigation, main slot, aside controls, footer). Hosts
 * sibling components: <td-storylist>, <td-account>, <td-vaultlock>,
 * <td-forgot-pass>, <td-reminder>, <td-acceptance>.
 *
 * Consumes `AccountData` from `dashboard.ts` (#163) to surface the
 * subscription tier as a header-side `<wa-badge>`. The full account card
 * still lives in `<td-account>` — this header badge is purely a
 * navigation-chrome affordance so the tier is visible without opening
 * the aside.
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { loadStory } from '../services/story.js';
import { fetchAccountData, type AccountData } from '../services/dashboard.js';
import {
  t,
  LocaleController,
  setUiLocale,
  getUiLocale,
  SUPPORTED_UI_LOCALES,
} from '../services/i18n.js';

/** Native-language display names for the UI-locale selector (#89). */
const UI_LOCALE_NAMES: Readonly<Record<string, string>> = {
  en: 'English',
  de: 'Deutsch',
};

/** Detail payload of the `logout` event the dashboard bubbles to `main.js`. */
export type LogoutEventDetail = void;

/** Lifecycle of the header tier-badge data (#239). */
export type AccountLoadState = 'loading' | 'ready' | 'error';

@customElement('td-dashboard')
export class TdDashboard extends LitElement {
  /**
   * Account data fetched on `connectedCallback` via the shared
   * single-flight loader in `dashboard.ts` (#239) — `<td-account>` and
   * friends reuse the same request. Drives the tier-level badge in the
   * header. `null` while the fetch is in flight or on failure;
   * `accountState` tells the two cases apart for rendering.
   */
  @property({ attribute: false }) accountData: AccountData | null = null;

  /**
   * Render phase of the tier badge (#239): `loading` shows a skeleton
   * placeholder, `ready` the badge, `error` renders nothing — the
   * header is chrome; `<td-account>` in the aside is the primary
   * surface for account-data problems.
   */
  @property({ attribute: false }) accountState: AccountLoadState = 'loading';

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this._loadAccountData();
  }

  private async _loadAccountData(): Promise<void> {
    this.accountState = 'loading';
    try {
      this.accountData = await fetchAccountData();
      this.accountState = 'ready';
    } catch (err) {
      console.warn('TdDashboard: failed to load account data', err);
      this.accountData = null;
      this.accountState = 'error';
    }
  }

  /**
   * Header tier-badge template, switched on `accountState` (#239):
   * skeleton while loading, `<wa-badge>` with the tier level when
   * ready, nothing on error (failure details surface in the aside's
   * `<td-account>` card and the console warning above).
   */
  private _tierBadgeTemplate(): TemplateResult | typeof nothing {
    if (this.accountState === 'loading') {
      return html`<wa-skeleton
        id="account-tier-skeleton"
        effect="sheen"
        data-test="account-tier-loading"
        style="width: 4rem"
      ></wa-skeleton>`;
    }
    if (this.accountState === 'ready' && this.accountData) {
      return html`<wa-badge
        id="account-tier-badge"
        variant="brand"
        pill
        data-test="account-tier-badge"
        >${this.accountData.level}</wa-badge
      >`;
    }
    return nothing;
  }

  /**
   * Click handler for `<wa-button id="logout-btn">`.
   *
   * Bound as a class-field arrow so `this` is auto-bound and the
   * reference is **stable across renders** — Lit re-creates inline
   * arrow handlers (`@click=${() => this._handleLogout()}`) every
   * render, which is the suspected cross-fire vector behind the
   * user-reported repro: clicking *any* button in the dashboard
   * subtree (e.g. `Add Participant` inside a `<td-story-form>`
   * mounted via `loadStory`) caused this handler to run alongside
   * the wired handler. A stable handler + the source-check guard
   * below close both classes of cause.
   *
   * If the guard ever rejects an event, the warning identifies the
   * mis-routed source so the underlying Lit / WebAwesome event-path
   * bug can be isolated rather than silently producing a spurious
   * logout.
   */
  _handleLogout = (event: Event): void => {
    const source = event.currentTarget as Element | null;
    if (!source || source.id !== 'logout-btn') {
      const tag = source?.tagName ?? '(no target)';
      const id = source?.id ?? '';
      console.warn(
        `TdDashboard._handleLogout: unexpected click source ${tag}#${id} — ignoring`,
        event
      );
      return;
    }
    this.dispatchEvent(
      new CustomEvent<LogoutEventDetail>('logout', { bubbles: true })
    );
  };

  /** UI-locale selector change → switch and persist the locale (#89). */
  _handleLocaleChange = (e: Event): void => {
    const value = (e.target as HTMLSelectElement | null)?.value;
    if (value) void setUiLocale(value);
  };

  _handleNewStory(e: Event): void {
    const btn = e.currentTarget as HTMLButtonElement | null;
    const mainContent = this.querySelector<HTMLElement>(
      '#main-insertion-point'
    );
    if (mainContent) {
      void loadStory(mainContent, btn);
    }
  }

  /**
   * Dashboard shell layout (#234): `<wa-page>` owns the page grid via
   * its own slots — `banner`, `header`, `navigation` (left rail on
   * desktop, built-in drawer + hamburger on mobile), `main-header`
   * (breadcrumbs), the default slot (main content), `aside` (action
   * buttons + account cards), and `main-footer`. The former
   * `.two-column` custom grid, the hand-rolled `data-toggle-nav`
   * hamburger, and the dead `mobile-navigation` slot (not part of
   * wa-page's API) are gone; the skip-to-content target div is
   * auto-injected by wa-page itself (firstUpdated), so the template no
   * longer declares one. Responsive behaviour now comes from wa-page's
   * `mobile-breakpoint` / `view` handling plus the
   * `wa-page[view='mobile']` rules in main.css.
   */
  override render(): TemplateResult {
    return html`
      <wa-page mobile-breakpoint="868" navigation-placement="start">
        <div class="top-notification" slot="banner">
          <a href="#">
            <wa-icon name="gift"></wa-icon>
            <span>${t('dashboard.banner')}</span>
          </a>
        </div>
        <header slot="header" class="wa-split">
          <span>
            <h1>Logo</h1>
            a nice one
          </span>
          ${this._tierBadgeTemplate()}
        </header>
        <nav
          slot="navigation"
          class="wa-stack wa-gap-xs"
          aria-label=${t('dashboard.navLabel')}
        >
          <a href="#">${t('dashboard.nav.home')}</a>
          <a href="#">${t('dashboard.nav.about')}</a>
          <a href="#">${t('dashboard.nav.contact')}</a>
          <a href="#">
            <wa-icon name="gears"></wa-icon>
            <span>${t('dashboard.nav.settings')}</span>
          </a>
        </nav>
        <div slot="main-header">
          <td-navigation
            id="main-navigation"
            crumbs=${t('dashboard.crumb')}
          ></td-navigation>
        </div>
        <main class="wa-body-l">
          <div id="main-insertion-point">
            <td-storylist></td-storylist>
          </div>
        </main>
        <aside slot="aside" aria-label=${t('dashboard.asideLabel')}>
          <wa-button
            id="logout-btn"
            pill
            appearance="filled outlined"
            variant="danger"
            @click=${this._handleLogout}
          >
            <wa-icon name="person-through-window" slot="start"></wa-icon>
            ${t('dashboard.logout')}
          </wa-button>
          <wa-button
            class="new-story-btn"
            pill
            appearance="filled outlined"
            variant="primary"
            @click=${(e: Event) => this._handleNewStory(e)}
          >
            ${t('dashboard.createNew')}
          </wa-button>
          <!-- #89: global UI-locale selector; the choice persists in
               localStorage and every localized component re-renders. -->
          <wa-select
            id="ui-locale-select"
            label=${t('dashboard.localeLabel')}
            .value=${getUiLocale()}
            @change=${this._handleLocaleChange}
          >
            ${SUPPORTED_UI_LOCALES.map(
              locale => html`
                <wa-option value=${locale}>
                  ${UI_LOCALE_NAMES[locale] ?? locale}
                </wa-option>
              `
            )}
          </wa-select>
          <td-account></td-account>
          <td-vaultlock></td-vaultlock>
          <!--
            #190: TdForgotPass mounts as a sibling of TdVaultLock. Always
            present in the DOM but only opens its wa-dialog in response
            to the "forgot-passphrase" event TdVaultLock dispatches on
            document. On successful recovery it dispatches
            "passphrase-recovered", which TdVaultLock catches to drive an
            automated unlock.
          -->
          <td-forgot-pass></td-forgot-pass>
          <td-reminder></td-reminder>
          <td-acceptance
            id="waiting-acceptance"
            title=${t('dashboard.acceptance.waiting')}
            type="waiting"
          ></td-acceptance>
          <td-acceptance
            id="pending-acceptance"
            title=${t('dashboard.acceptance.pending')}
            type="pending"
          ></td-acceptance>
        </aside>

        <footer slot="main-footer" class="wa-grid wa-gap-xl">
          <div class="wa-stack">
            <h3 class="wa-heading-xs">${t('dashboard.footer.service')}</h3>
            <a href="#">${t('dashboard.footer.privacy')}</a>
            <a href="#">${t('dashboard.footer.terms')}</a>
            <a href="#">${t('dashboard.footer.contact')}</a>
          </div>
          <div class="wa-stack">
            <h3 class="wa-heading-xs">${t('dashboard.footer.aboutUs')}</h3>
            <a href="#">${t('dashboard.footer.aboutTd')}</a>
            <a href="#">${t('dashboard.footer.mission')}</a>
            <a href="#">${t('dashboard.footer.team')}</a>
          </div>
          <div class="wa-stack">
            <h3 class="wa-heading-xs">${t('dashboard.footer.discover')}</h3>
            <a href="#">${t('dashboard.footer.products')}</a>
            <a href="#">${t('dashboard.footer.services')}</a>
            <a href="#">${t('dashboard.footer.blog')}</a>
          </div>
        </footer>
      </wa-page>
    `;
  }
}
